import { useEffect, useState } from 'react'
import { matchPath, NavLink, useLocation } from 'react-router-dom'
import { SidebarConfigProps } from '@/reducers/sidebar.slice'

import { mtc, TextColors, TypographyStyles } from '@/styles'
import { Accordion, Badge, Icon, List, Popover, Text, Tooltip } from '@/ui'
import {
  pageAccordionTriggerStyles,
  pageDivItemStyles,
  pageItemStyles,
  pageListItemStyles,
} from './stylesRestructured'

export interface NavAnnouncementStep {
  // Matches against category `label`s to anchor the popover to. Adjacent
  // top-level flat items sharing a step are grouped under one anchor/popover
  // rather than shown as separate popovers.
  anchorLabels: string[]
  // A page label inside a matched category's `pages`. Once that category is
  // expanded, the anchor dot moves off the section header and onto this
  // nested page instead (e.g. off "Reports" and onto "Profit & Loss").
  nestedAnchorLabel?: string
  content: React.ReactNode
}

export interface NavAnnouncementProps {
  steps: NavAnnouncementStep[]
  activeStepIndex: number
  open: boolean
  // Renders the attention dot for a given item/page label, next to whichever
  // sidebar item the active step is anchored to — not inside the popover
  // card itself. A function (not a single node) so each anchored item gets
  // its own dot with its own visited/visible state.
  renderAnchorIndicator?: (label: string) => React.ReactNode
  // Called when the user clicks into an anchored item or nested page, so its
  // dot can fade out and stay gone regardless of the tour step.
  onAnchorItemClick?: (label: string) => void
  // Called when the announcement actually renders. The hook that owns it can't
  // tell on its own: the sidebar collapses itself on small viewports, when the
  // Haven panel opens, and when the user collapses it, and this list stops
  // rendering entirely in all three cases.
  onDisplay?: () => void
}

export interface NavListRestructuredProps {
  categories?: Record<string, SidebarConfigProps> | null
  className?: string
  disabled?: boolean
  selectedCategory?: string | null
  navAnnouncement?: NavAnnouncementProps
}

export const NavListRestructured = ({
  categories,
  disabled = false,
  selectedCategory,
  navAnnouncement,
  ...rest
}: NavListRestructuredProps) => {
  const location = useLocation()
  // Controlled accordion so open sections survive the transient `return null`
  // while `useSidebarConfig` reloads (e.g. navigating to Annual Taxes triggers
  // a tax-data fetch). An uncontrolled accordion would remount and collapse.
  const [openKeys, setOpenKeys] = useState<string[]>([])

  const allEntries = categories ? Object.entries(categories) : []

  const splitPoint = allEntries.findIndex(
    ([, cat]) => cat.pages !== null && cat.pages !== undefined
  )
  const topEntries = splitPoint > 0 ? allEntries.slice(0, splitPoint) : []
  const mainEntries = splitPoint > 0 ? allEntries.slice(splitPoint) : allEntries

  const activeAccordionKeys = mainEntries.reduce<string[]>(
    (acc, [key, category]) => {
      if (!category.pages) return acc

      const hasActivePage = category.pages.some(
        (page) =>
          page.to && matchPath({ path: page.to, end: false }, location.pathname)
      )

      if (hasActivePage) acc.push(key)
      if (selectedCategory === key && categories?.[selectedCategory]?.pages) {
        acc.push(key)
      }

      return acc
    },
    []
  )

  // Auto-open the section for the active route without collapsing ones the user
  // opened manually. Never removes keys, so navigation only ever expands.
  const activeKeysSignature = activeAccordionKeys.join(',')
  useEffect(() => {
    if (activeAccordionKeys.length) {
      setOpenKeys((prev) =>
        Array.from(new Set([...prev, ...activeAccordionKeys]))
      )
    }
    // activeAccordionKeys is a new array every render, so depending on it directly
    // causes an infinite loop. Depending on the stable string signature is equivalent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKeysSignature])

  // Reported from here rather than from the announcement's own hook, so a
  // collapsed sidebar correctly counts as no display.
  const isAnnouncementDisplayed = Boolean(navAnnouncement?.open && categories)
  const onAnnouncementDisplay = navAnnouncement?.onDisplay
  useEffect(() => {
    if (isAnnouncementDisplayed) onAnnouncementDisplay?.()
  }, [isAnnouncementDisplayed, onAnnouncementDisplay])

  if (!categories) {
    return null
  }

  const activeAnnouncementStep = navAnnouncement?.open
    ? navAnnouncement.steps[navAnnouncement.activeStepIndex]
    : undefined
  const announcementAnchorLabels = new Set(
    activeAnnouncementStep?.anchorLabels ?? []
  )

  // Adjacent top-level items sharing the active step's anchorLabels (e.g.
  // Transactions + Documents) are grouped under a single popover anchor
  // rather than shown as separate popovers.
  interface TopGroup {
    groupKey: string
    entries: Array<[string, SidebarConfigProps]>
    isAnnouncementAnchor: boolean
  }
  const topGroups = topEntries.reduce<TopGroup[]>((groups, [key, category]) => {
    const isAnchor = announcementAnchorLabels.has(category.label)
    const lastGroup = groups[groups.length - 1]
    if (isAnchor && lastGroup?.isAnnouncementAnchor) {
      lastGroup.entries.push([key, category])
    } else {
      groups.push({
        groupKey: key,
        entries: [[key, category]],
        isAnnouncementAnchor: isAnchor,
      })
    }
    return groups
  }, [])

  // Active when the current path matches the item's `to` or its `match` alias.
  const isCategoryActive = (category: SidebarConfigProps) => {
    const matchAlias = 'match' in category ? category.match : undefined
    const matchAliases = Array.isArray(matchAlias) ? matchAlias : [matchAlias]
    const candidates = [category.to, ...matchAliases].filter((p): p is string =>
      Boolean(p)
    )
    return candidates.some((path) =>
      matchPath(
        { path, end: Boolean(category.end ?? category.exact) },
        location.pathname
      )
    )
  }

  return (
    <div>
      {topEntries.length > 0 && (
        <>
          <div className="space-y-0.5">
            {topGroups.map((group) => {
              const items = group.entries.map(([key, category]) => {
                const isActive = isCategoryActive(category)
                const isAnnouncementAnchorItem = announcementAnchorLabels.has(
                  category.label
                )
                return (
                  <div
                    key={key}
                    className={mtc(pageDivItemStyles({ isActive }))}
                  >
                    <NavLink
                      end={category.end}
                      to={category.to ?? ''}
                      className="flex items-center w-full h-full pl-2 pr-2"
                      onClick={() => {
                        if (isAnnouncementAnchorItem) {
                          navAnnouncement?.onAnchorItemClick?.(category.label)
                        }
                      }}
                    >
                      <div className="flex items-center gap-3 min-w-0 overflow-hidden">
                        {category.icon && (
                          <Icon
                            icon={category.icon}
                            size="1x"
                            className="w-5 h-5 shrink-0"
                            backgroundClassName="w-5 h-5 shrink-0"
                          />
                        )}
                        <Tooltip>
                          <Tooltip.Trigger asChild>
                            <Text
                              variant={TypographyStyles.H3}
                              className="truncate"
                            >
                              {category.label}
                            </Text>
                          </Tooltip.Trigger>
                          <Tooltip.Content side="top" align="start">
                            <Text variant={TypographyStyles.BODY_SM}>
                              {category.label}
                            </Text>
                          </Tooltip.Content>
                        </Tooltip>
                        {isAnnouncementAnchorItem &&
                          navAnnouncement?.renderAnchorIndicator?.(
                            category.label
                          )}
                        {category.ornament && (
                          <span className="shrink-0 flex items-center">
                            {category.ornament}
                          </span>
                        )}
                      </div>
                    </NavLink>
                  </div>
                )
              })

              // Non-anchor groups are always a single item (grouping only merges
              // consecutive anchor matches), so render it directly rather than
              // wrapping it in an extra, functionally inert div.
              if (!group.isAnnouncementAnchor) {
                return items[0]
              }

              return (
                <Popover key={group.groupKey} open={navAnnouncement?.open}>
                  <Popover.Anchor asChild>
                    <div className="space-y-0.5">{items}</div>
                  </Popover.Anchor>
                  <Popover.Content
                    side="right"
                    align="start"
                    className="bg-neutral-0"
                  >
                    {activeAnnouncementStep?.content}
                  </Popover.Content>
                </Popover>
              )
            })}
          </div>
          <div className="-mx-4 py-4" aria-hidden="true">
            <div className="border-t border-neutral-50" />
          </div>
        </>
      )}

      <Accordion
        type="multiple"
        value={openKeys}
        onValueChange={setOpenKeys}
        className="space-y-0.5"
      >
        {mainEntries.map(([key, category]) => {
          const isActive = isCategoryActive(category)

          if (category.pages) {
            const isAnnouncementAnchorItem = announcementAnchorLabels.has(
              category.label
            )
            const isExpanded = openKeys.includes(key)
            // Once expanded, the dot belongs on the nested page the step
            // actually refers to, not the section header.
            const showHeaderIndicator = isAnnouncementAnchorItem && !isExpanded
            const accordionItem = (
              <Accordion.Item
                key={key}
                value={key}
                className={mtc(pageItemStyles({ disabled }))}
                {...rest}
              >
                <Accordion.Trigger
                  className={mtc(pageAccordionTriggerStyles({ isActive }))}
                >
                  <div className="flex items-center gap-3 min-w-0 overflow-hidden">
                    {category.icon && (
                      <Icon
                        icon={category.icon}
                        size="1x"
                        className="w-5 h-5 shrink-0"
                        backgroundClassName="w-5 h-5 shrink-0"
                      />
                    )}
                    <Tooltip>
                      <Tooltip.Trigger asChild>
                        <Text
                          variant={TypographyStyles.H3}
                          className="truncate"
                        >
                          {category.label}
                        </Text>
                      </Tooltip.Trigger>
                      <Tooltip.Content side="top" align="start">
                        <Text variant={TypographyStyles.BODY_SM}>
                          {category.label}
                        </Text>
                      </Tooltip.Content>
                    </Tooltip>
                    {showHeaderIndicator &&
                      navAnnouncement?.renderAnchorIndicator?.(category.label)}
                  </div>
                </Accordion.Trigger>

                <Accordion.Content className="pb-0">
                  <List className="pl-0">
                    {category.pages.map(
                      ({
                        label,
                        to,
                        match,
                        end,
                        isNewFeature,
                        icon,
                        newPage,
                        isBetaFeature,
                      }) => {
                        const isPageActive = [to, match]
                          .filter((p): p is string => Boolean(p))
                          .some((path) =>
                            matchPath({ path, end: false }, location.pathname)
                          )
                        const isNestedAnnouncementAnchor =
                          isExpanded &&
                          isAnnouncementAnchorItem &&
                          activeAnnouncementStep?.nestedAnchorLabel === label
                        return (
                          <List.Item
                            key={label}
                            className={mtc(
                              pageListItemStyles({ isActive: isPageActive })
                            )}
                          >
                            <NavLink
                              end={end}
                              to={to ?? ''}
                              className="flex items-center w-full h-full pl-10 pr-2"
                              target={newPage ? '_blank' : undefined}
                              onClick={() => {
                                if (isNestedAnnouncementAnchor) {
                                  navAnnouncement?.onAnchorItemClick?.(label)
                                }
                              }}
                            >
                              <div className="flex items-center gap-1 min-w-0 overflow-hidden">
                                <Tooltip>
                                  <Tooltip.Trigger asChild>
                                    <Text
                                      variant={TypographyStyles.LABEL_SM}
                                      className="truncate text-neutral-600"
                                    >
                                      {label}
                                    </Text>
                                  </Tooltip.Trigger>
                                  <Tooltip.Content side="top" align="start">
                                    <Text variant={TypographyStyles.BODY_SM}>
                                      {label}
                                    </Text>
                                  </Tooltip.Content>
                                </Tooltip>
                                {icon && (
                                  <Icon
                                    icon={icon}
                                    size="1x"
                                    className="shrink-0"
                                  />
                                )}
                                {isNestedAnnouncementAnchor && (
                                  <span className="ml-2 shrink-0">
                                    {navAnnouncement?.renderAnchorIndicator?.(
                                      label
                                    )}
                                  </span>
                                )}
                              </div>
                              {isNewFeature && (
                                <Badge
                                  color="yellow"
                                  className="ml-4 shrink-0"
                                  size="sm"
                                >
                                  <Text
                                    variant={TypographyStyles.EYEBROW}
                                    color={TextColors.WHITE}
                                  >
                                    NEW
                                  </Text>
                                </Badge>
                              )}
                              {isBetaFeature && (
                                <Text
                                  variant={TypographyStyles.EYEBROW}
                                  color={TextColors.GREEN}
                                  className="ml-4 shrink-0"
                                >
                                  BETA
                                </Text>
                              )}
                            </NavLink>
                          </List.Item>
                        )
                      }
                    )}
                  </List>
                </Accordion.Content>
              </Accordion.Item>
            )

            if (!isAnnouncementAnchorItem) {
              return accordionItem
            }

            return (
              <Popover key={key} open={navAnnouncement?.open}>
                {/* Accordion.Item isn't a forwardRef component, so Popover.Anchor
                    needs a plain DOM node here to get a real ref to position against. */}
                <Popover.Anchor asChild>
                  <div>{accordionItem}</div>
                </Popover.Anchor>
                <Popover.Content
                  side="right"
                  align="start"
                  className="bg-neutral-0"
                >
                  {activeAnnouncementStep?.content}
                </Popover.Content>
              </Popover>
            )
          }

          if (category.isSpacer) {
            return (
              <div key={key} className="-mx-4 py-4" aria-hidden="true">
                <div className="border-t border-neutral-50" />
              </div>
            )
          }

          return (
            <div key={key} className={mtc(pageDivItemStyles({ isActive }))}>
              <NavLink
                end={category.end}
                to={category.to ?? ''}
                className={mtc('flex items-center w-full h-full pl-2 pr-2')}
              >
                <div className="flex items-center gap-3 min-w-0 overflow-hidden">
                  {category.icon && (
                    <Icon
                      icon={category.icon}
                      size="1x"
                      className="w-5 h-5 shrink-0"
                      backgroundClassName="w-5 h-5 shrink-0"
                    />
                  )}
                  <Tooltip>
                    <Tooltip.Trigger asChild>
                      <Text variant={TypographyStyles.H3} className="truncate">
                        {category.label}
                      </Text>
                    </Tooltip.Trigger>
                    <Tooltip.Content side="top" align="start">
                      <Text variant={TypographyStyles.BODY_SM}>
                        {category.label}
                      </Text>
                    </Tooltip.Content>
                  </Tooltip>
                  {category.ornament && (
                    <span className="shrink-0 flex items-center">
                      {category.ornament}
                    </span>
                  )}
                  {category.isNewFeature && (
                    <Text
                      variant={TypographyStyles.EYEBROW}
                      color={TextColors.GREEN}
                      className="ml-4 shrink-0"
                    >
                      NEW
                    </Text>
                  )}
                </div>
              </NavLink>
            </div>
          )
        })}
      </Accordion>
    </div>
  )
}
